import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { setAuthToken, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { t } from "../lib/i18n";

// Reintenta una llamada async con backoff. El ingress de Emergent puede devolver 404
// transitorios durante rotaciones/pod restarts; nuestro backend valida y devuelve 401
// para session_ids inválidos. Un 404 casi siempre es transitorio.
async function callWithRetry(fn, { retries = 2, delayMs = 1200 } = {}) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (e) {
      lastErr = e;
      const status = e?.response?.status;
      const retriable = status === 404 || status === 502 || status === 503 || status === 504 || !status;
      if (!retriable || attempt === retries) break;
      await new Promise((r) => setTimeout(r, delayMs * (attempt + 1)));
    }
  }
  throw lastErr;
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [debug, setDebug] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    // session_id llega en el fragmento (`#session_id=...`) según el playbook de Emergent Auth,
    // aunque algunos intermediarios convierten `#` a `?`. Aceptamos ambos.
    const hash = window.location.hash || "";
    const search = window.location.search || "";
    const hashParams = new URLSearchParams(hash.replace(/^#/, ""));
    const queryParams = new URLSearchParams(search);
    const sessionId = hashParams.get("session_id") || queryParams.get("session_id");

    if (!sessionId) {
      setDebug(`URL: ${window.location.href}`);
      setErrorMsg("No se ha recibido session_id desde Google. Si esto persiste, comparte una captura de esta pantalla.");
      return;
    }

    (async () => {
      try {
        const r = await callWithRetry(() => api.post("/auth/session", { session_id: sessionId }));
        if (r.data.token) setAuthToken(r.data.token);
        const u = r.data.user;
        setUser(u);
        // limpiamos fragment/query
        window.history.replaceState(null, "", window.location.pathname);
        if (!u.role) navigate("/role-select");
        else if (u.role === "seller") navigate("/seller/dashboard");
        else if (u.role === "investor") navigate("/investor/marketplace");
        else navigate("/searcher/marketplace");
      } catch (e) {
        console.error("auth callback failed", e);
        const status = e?.response?.status;
        const backendDetail = e?.response?.data?.detail;
        // Cuando el error es 404 sin body (típico del ingress), damos un mensaje accionable.
        const isIngress404 = status === 404 && !backendDetail;
        const detail = backendDetail || e?.message || "Fallo desconocido";
        setDebug([
          `endpoint=${API}/auth/session`,
          `status=${status || "n/a"}`,
          `detail=${typeof detail === "string" ? detail : JSON.stringify(detail)}`,
          isIngress404 ? "hint=ingress-404 (reintenta o abre en pestaña nueva)" : null,
        ].filter(Boolean).join(" · "));
        setErrorMsg(
          isIngress404
            ? "El servicio de autenticación no respondió a tiempo. Prueba a volver al login e intentarlo de nuevo (o abre la web en una pestaña nueva)."
            : "No se pudo establecer la sesión."
        );
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="text-center space-y-4 max-w-xl" data-testid="auth-callback">
        {!errorMsg ? (
          <>
            <span className="font-mono-label text-gold">{t("Verificando sesión")}</span>
            <p className="font-serif-display text-2xl">{t("Estamos validando tu acceso…")}</p>
          </>
        ) : (
          <>
            <span className="font-mono-label text-destructive">{t("Login con Google: error")}</span>
            <p className="font-serif-display text-2xl">{errorMsg}</p>
            {debug && (
              <pre
                className="hairline bg-muted/30 text-left text-xs p-3 overflow-auto"
                data-testid="auth-callback-debug"
              >
                {debug}
              </pre>
            )}
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="font-mono-label text-gold underline"
              data-testid="auth-callback-back-login"
            >
              {t("Volver al login")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
